/**
 * Which product a comparison page is ABOUT (FW-ENTITY-01).
 *
 * Two "alternatives" pages about different products carry the same topic and
 * look identical to topic matching. `/blog/monday-com-competitors` was told it
 * lacked `float-alternatives`, `ganttic-alternatives` and
 * `resource-guru-alternatives` — all resource-management pages, none about
 * monday.com. The owner's rule: "it's fine that 'resource management' and
 * 'time tracking' is a match when we talk about monday or float, but it's not
 * fine that 'float' is a match when we talk about 'monday'."
 *
 * The entity is read from the URL slug, the most reliable signal in this
 * corpus: `monday-com-competitors` → `monday`, `float-alternatives` → `float`.
 * A slug with no comparison marker names NO entity — `resource-management`,
 * `capacity-management` — and such a page is never gated by this rule, which
 * is what keeps topic matching working for the 90% of pages that are about a
 * subject rather than a product.
 *
 * MIRROR: `backend/src/utils/page-entity.ts` is a verbatim copy. The backend
 * test `page-entity-mirror.test.ts` fails when the two files differ — edit
 * both or neither.
 *
 * Pure: no Angular, no DOM.
 */

/**
 * Slug tokens that frame a product comparison; the entity sits before them.
 * `comparison` is deliberately absent: on the live corpus its only slug hit was
 * `what-is-a-comparison-chart-and-how-do-you-use-it`, which is not about a
 * product, and a false entity on a FOCAL page would gate every rival off it.
 */
const COMPARISON_MARKER = /-(?:alternatives?|competitors?)(?:-|$)/;

/** `alternatives-to-monday` frames the entity AFTER the marker. */
const ALTERNATIVES_TO = /^(?:alternatives?|competitors?)-to-(.+)$/;

const VS_SPLIT = /-(?:vs|versus)-/;

/**
 * A path whose parent segment is one of these frames the last segment as the
 * entity: `/alternatives/client-portal-io`, `/compare/asana`.
 */
const FRAME_SEGMENTS = new Set(['alternatives', 'alternative', 'compare', 'comparison', 'vs', 'versus']);

/** Leading qualifiers: `10-best-asana-alternatives` is about asana. */
const LEADING_QUALIFIERS = new Set(['best', 'top', 'free', 'cheap', 'cheaper', 'good', 'great', 'popular', 'the', 'a']);

/**
 * Where a `vs`-frame entity stops: `asana-vs-monday-which-is-better`,
 * `monday-vs-asana-2026`. Only applied to segments AFTER a `vs` split, where
 * the tail is not delimited by anything else.
 */
const TAIL_WORDS = new Set([
  'which', 'what', 'whats', 'how', 'why', 'should', 'for', 'in', 'and', 'or', 'the', 'a', 'an',
  'is', 'to', 'with', 'top', 'best', 'better', 'two', 'compared', 'comparing', 'comparison',
  'review', 'reviewed', 'pricing', 'features', 'alternative', 'alternatives', 'competitor',
  'competitors', 'guide', 'explained'
]);

/** One corporate suffix is dropped: `monday-com` → `monday`, `productive-io` → `productive`. */
const CORPORATE_SUFFIXES = new Set(['com', 'io', 'app', 'ai']);

function pathSegments(url: string): string[] {
  const raw = (url || '').trim();
  if (!raw) return [];
  let path = raw;
  try {
    path = new URL(raw).pathname;
  } catch {
    // Not a full URL — treat the string as a path or bare slug.
  }
  return path
    .toLowerCase()
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function isNumeric(token: string): boolean {
  return /^\d+$/.test(token);
}

function stripLeadingQualifiers(tokens: string[]): string[] {
  let i = 0;
  while (i < tokens.length && (isNumeric(tokens[i]) || LEADING_QUALIFIERS.has(tokens[i]))) i++;
  return tokens.slice(i);
}

function cutAtTail(tokens: string[]): string[] {
  const end = tokens.findIndex((token) => TAIL_WORDS.has(token) || /^\d{4}$/.test(token));
  return end === -1 ? tokens : tokens.slice(0, end);
}

function stripCorporateSuffix(tokens: string[]): string[] {
  if (tokens.length > 1 && CORPORATE_SUFFIXES.has(tokens[tokens.length - 1])) {
    return tokens.slice(0, -1);
  }
  return tokens;
}

/** Lowercase, alphanumerics only: `resource-guru` and `Resource Guru` compare equal. */
export function normalizeEntity(value: string): string {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Two normalised entities name the same product. The corporate suffix is
 * stripped only when it is its own slug token, so `pop-ai` becomes `pop` while
 * `popai` stays whole; the two are the same product and compare equal here.
 * (Stripping at string level instead would turn `bonsai` into `bons`.)
 */
export function sameEntity(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  for (const suffix of CORPORATE_SUFFIXES) {
    if (a + suffix === b || b + suffix === a) return true;
  }
  return false;
}

function finishCandidate(candidate: string, options: { cutTail: boolean }): string | null {
  let tokens = candidate.split('-').filter(Boolean);
  tokens = stripLeadingQualifiers(tokens);
  if (options.cutTail) tokens = cutAtTail(tokens);
  tokens = stripCorporateSuffix(tokens);
  const entity = normalizeEntity(tokens.join(''));
  return entity || null;
}

function entitiesFromSlug(slug: string, parentSegment: string | undefined): string[] {
  const candidates: Array<{ text: string; cutTail: boolean }> = [];

  const vsParts = slug.split(VS_SPLIT);
  if (vsParts.length > 1) {
    for (const part of vsParts) {
      // A trailing frame word on the last part: `assembly-vs-karbon-alternative`.
      const cleaned = part.replace(COMPARISON_MARKER, '-').replace(/-+$/, '');
      candidates.push({ text: cleaned, cutTail: true });
    }
  } else {
    const after = slug.match(ALTERNATIVES_TO);
    const before = slug.match(COMPARISON_MARKER);
    if (after) {
      candidates.push({ text: after[1], cutTail: true });
    } else if (before && before.index !== undefined && before.index > 0) {
      candidates.push({ text: slug.slice(0, before.index), cutTail: false });
    } else if (parentSegment && FRAME_SEGMENTS.has(parentSegment)) {
      candidates.push({ text: slug, cutTail: false });
    }
  }

  const out: string[] = [];
  for (const candidate of candidates) {
    const entity = finishCandidate(candidate.text, { cutTail: candidate.cutTail });
    if (entity && !out.includes(entity)) out.push(entity);
  }
  return out;
}

/**
 * Title fallback, used only when the slug names nothing: "Monday.com vs Asana",
 * "10 Best Float Alternatives (2026)". The word immediately before the marker
 * is the entity; a leading qualifier ("Best") is skipped.
 */
function entitiesFromTitle(title: string): string[] {
  const text = (title || '').toLowerCase().replace(/[^a-z0-9.\s]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return [];
  const words = text.split(' ');
  const out: string[] = [];
  const push = (raw: string) => {
    const entity = finishCandidate(raw.replace(/\./g, '-'), { cutTail: false });
    if (entity && !out.includes(entity)) out.push(entity);
  };

  const vsIndex = words.findIndex((word) => word === 'vs' || word === 'versus' || word === 'vs.');
  if (vsIndex > 0 && vsIndex < words.length - 1) {
    push(words[vsIndex - 1]);
    push(words[vsIndex + 1]);
    return out;
  }

  const markerIndex = words.findIndex((word) => /^(?:alternatives?|competitors?)$/.test(word));
  if (markerIndex > 0) {
    const previous = words[markerIndex - 1];
    if (!LEADING_QUALIFIERS.has(previous) && !isNumeric(previous)) push(previous);
  }
  return out;
}

/**
 * Every product a page names in a comparison frame, normalised, first the most
 * prominent. `monday-com-vs-teamwork-vs-scoro` → `['monday', 'teamwork', 'scoro']`.
 * Empty when the page names none.
 */
export function pageEntities(url: string, title?: string): string[] {
  const segments = pathSegments(url);
  if (segments.length) {
    const slug = segments[segments.length - 1];
    const parent = segments.length > 1 ? segments[segments.length - 2] : undefined;
    const fromSlug = entitiesFromSlug(slug, parent);
    if (fromSlug.length) return fromSlug;
  }
  return title ? entitiesFromTitle(title) : [];
}

/**
 * The product a page is about, or `null` when it names none.
 *
 * A multi-product slug (`monday-com-vs-teamwork-vs-scoro`) answers with the
 * FIRST product: in this corpus the first slot is the page's own subject and
 * the rest are what it is measured against. Callers that need the whole set
 * use {@link pageEntities}; the competing-entity gate does, so a rival page
 * that names our product anywhere in its slug is still kept.
 */
export function pageEntity(url: string, title?: string): string | null {
  return pageEntities(url, title)[0] ?? null;
}
